import {useMemo} from 'react';

import {usePart} from '../../../../state/views/hooks';
import type * as PanelTypes from '../../../../state/views/panel/types';
import {useDeepEqualValue} from '../../../../util/hooks';
import {
  useFullScreenLinePlotMaxRunsDynamicConfig,
  useLinePlotMaxRunsDynamicConfig,
} from '../../../../util/rampFeatureFlags/linePlotDynamicConfigs';
import {
  useRampFlagFullFidelityRunsPerChart,
  useRampFlagIncreaseFullScreenMaxRuns,
} from '../../../../util/rampFeatureFlags/linePlotFeatureFlags';
import {useIsFullScreenPanel} from '../../../Panel/hooks/useIsFullScreenPanel';
import {useDefinedMetricContext} from '../../../PanelBank/contextProviders/DefinedMetricContext';
import {setPanelStartingXAxisFromGlobMatch} from '../../../PanelBank/contextProviders/defineMetricUtils';
import {type OrganizedSettings} from '../../../PanelBank/types';
import {type MediaBrowserPanelConfig} from '../../../PanelMediaBrowser/common';
import {type RunsLinePlotConfig} from '../../../PanelRunsLinePlot/types';
import {getCascadingSetting} from '../getCascadingSetting';
import {getCascadingExcludeOutlierSetting} from '../runLinePlots/utils/getCascadingExcludeOutliers';
import {clampMaxRunsForLinePlot} from '../runLinePlots/utils/getCascadingMaxRuns';
import {useCascadingPointAggregationMethod} from '../runLinePlots/utils/getCascadingPointAggregation';
import {getCascadingShowLegend} from '../runLinePlots/utils/getCascadingShowLegend';
import {getCascadingSmoothingSettings} from '../runLinePlots/utils/getCascadingSmoothingSettings';
import {getCascadingXAxisSettings} from '../runLinePlots/utils/getCascadingXAxisSettings';
import {getCascadingYAxisSettings} from '../runLinePlots/utils/getCascadingYAxisSettings';
import {
  type DerivedPointVisualizationOption,
  isPointVizFullFidelity,
  type LinePlotSettings,
} from '../types';
/**
 * This hook determine if the parent cascading settings or panel setting
 * should be used. It only gets used in PanelCompRedux.
 *
 * Hierarchy: panelConfig >> (sectionSettings >> workspaceSettings)
 *
 * @param inheritedSettings either the workspace or section setting
 */
export const usePanelWithInheritedSettings = (
  panelRef: PanelTypes.Ref,
  inheritedSettings: OrganizedSettings | undefined
) => {
  const panel = usePart(panelRef);
  const {definedGlobs} = useDefinedMetricContext();
  const canOverrideMaxRuns = useRampFlagFullFidelityRunsPerChart();
  const canIncreaseFullScreenMaxRuns = useRampFlagIncreaseFullScreenMaxRuns();
  const dynamicConfigMaxRuns = useLinePlotMaxRunsDynamicConfig();
  const dynamicConfigFullScreenMaxRuns =
    useFullScreenLinePlotMaxRunsDynamicConfig();
  const {isFullScreen} = useIsFullScreenPanel();
  const isRunsLinePlot = panel.viewType === 'Run History Line Plot';

  const cascadingPointAggregationSettings = useCascadingPointAggregationMethod(
    inheritedSettings?.linePlot,
    isRunsLinePlot ? panel.config : undefined
  );

  const modifiedPanel = useMemo(() => {
    if (inheritedSettings == null) {
      return panel;
    }

    // We need to pipe through useRunsTableGroupingInPanels so we can show
    // individual runs in the run comparer
    if (panel.viewType === 'Run Comparer') {
      return {
        ...panel,
        config: {
          ...panel.config,
          // section setting doesn't exist, so inherit workspace value
          useRunsTableGroupingInPanels:
            inheritedSettings.linePlot?.useRunsTableGroupingInPanels,
        },
      };
    }

    if (isRunsLinePlot) {
      const globInjectedPanelConfig = setPanelStartingXAxisFromGlobMatch(
        panel.config,
        definedGlobs
      );

      const configSettings = getLinePlotPanelConfigWithInheritedSettings(
        cascadingPointAggregationSettings.pointVisualizationMethod,
        inheritedSettings.linePlot,
        globInjectedPanelConfig
      );

      const throttledLimit = clampMaxRunsForLinePlot(
        {
          isBucketing: isPointVizFullFidelity(
            cascadingPointAggregationSettings.pointVisualizationMethod
          ),
          canOverrideMaxRuns,
          isFullScreen,
          canIncreaseFullScreenMaxRuns,
          dynamicConfigMaxRuns,
          dynamicConfigFullScreenMaxRuns,
        },
        configSettings.limit
      );

      return {
        ...panel,
        config: {
          ...configSettings,
          limit: throttledLimit,
        },
      };
    }
    if (panel.viewType === 'Media Browser') {
      // We initially rolled out bulk media settings with chart title, but later decided to
      // remove it. This makes it so that any previous users who set their chart title at a
      // higher level will have the chart title ignored.
      const {chartTitle: _chartTitle, ...higherLevelSettings} =
        inheritedSettings?.media ?? {};
      const newSettings = {
        ...panel,
        config: getCascadedMediaSettings(higherLevelSettings, panel.config),
      };
      return newSettings;
    }

    return panel;
  }, [
    canIncreaseFullScreenMaxRuns,
    canOverrideMaxRuns,
    cascadingPointAggregationSettings,
    definedGlobs,
    dynamicConfigFullScreenMaxRuns,
    dynamicConfigMaxRuns,
    inheritedSettings,
    isFullScreen,
    isRunsLinePlot,
    panel,
  ]);

  const deepEqualPanel = useDeepEqualValue(modifiedPanel);

  return deepEqualPanel;
};

/**
 *
 * Lower level settings should always take precedence over higher level settings.
 */
export function getCascadedMediaSettings(
  higherLevelSettings: MediaBrowserPanelConfig | undefined,
  lowerLevelSettings: MediaBrowserPanelConfig | undefined
): MediaBrowserPanelConfig {
  return {
    ...higherLevelSettings,
    ...lowerLevelSettings,
    // reversed override, videoPlaybackSync should be inherited from the higher level
    videoPlaybackSync:
      higherLevelSettings?.videoPlaybackSync ??
      lowerLevelSettings?.videoPlaybackSync,
  };
}

/**
 * Modifies runs line plot panel config with inherited settings.
 * Panel settings should always take precedence over workspace and section settings.
 */
export const getLinePlotPanelConfigWithInheritedSettings = (
  inheritedPointVisualizationMethod: DerivedPointVisualizationOption,
  inheritedSettings: LinePlotSettings | undefined,
  panelConfig: RunsLinePlotConfig
) => {
  const newPanelConfig = {
    // Order matters here
    ...panelConfig, // settings that belong at panel level and cannot be updated at a higher level
    ...getInheritedPanelSettings(
      inheritedPointVisualizationMethod,
      inheritedSettings,
      panelConfig
    ),
  };

  // @ts-expect-error - ignoreOutliers is deprecated, make sure it doesn't show up downstream
  delete newPanelConfig.ignoreOutliers;
  // @ts-expect-error - suppressLegends is deprecated, make sure it doesn't show up downstream
  delete newPanelConfig.suppressLegends;

  return newPanelConfig;
};

const getInheritedPanelSettings = (
  inheritedPointVisualizationMethod: DerivedPointVisualizationOption,
  // This should be the resolved settings between workspace and section settings
  inheritedSettings: LinePlotSettings | undefined,
  panelSettings?: RunsLinePlotConfig
) => {
  return {
    // Workspace vs section settings are resolved at a higher level, so
    // pass undefined for child/section settings. For certain settings,
    // we apply different logic at panel settings like x-axis system metric panels.
    ...getCascadingXAxisSettings(inheritedSettings, undefined, panelSettings),
    ...getCascadingSmoothingSettings(
      inheritedSettings,
      undefined,
      panelSettings
    ),
    ...getCascadingYAxisSettings(inheritedSettings, undefined, panelSettings),
    showOriginalAfterSmoothing: getCascadingSetting([
      inheritedSettings?.showOriginalAfterSmoothing,
      panelSettings?.showOriginalAfterSmoothing,
    ]),
    // This setting don't exist at panel level, so just take the parent values
    colorRunNames: inheritedSettings?.colorRunNames,
    coordinateZooming: inheritedSettings?.coordinateZooming ?? false,
    // This setting don't exist at panel level, so just take the parent values
    displayFullRunName: inheritedSettings?.displayFullRunName,
    excludeOutliers: getCascadingExcludeOutlierSetting(
      inheritedPointVisualizationMethod,
      inheritedSettings,
      undefined,
      panelSettings
    ),
    groupAgg: getCascadingSetting([
      inheritedSettings?.groupAgg,
      panelSettings?.groupAgg,
    ]),
    highlightedCompanionRunOnly: inheritedSettings?.highlightedCompanionRunOnly,
    limit: getCascadingSetting([
      inheritedSettings?.limit,
      panelSettings?.limit,
    ]),
    // This is calculated at a higher level, so just take the parent value
    pointVisualizationMethod: inheritedPointVisualizationMethod,
    showLegend: getCascadingSetting([
      getCascadingShowLegend({
        // @ts-expect-error - suppressLegends is deprecated
        suppressLegends: inheritedSettings?.suppressLegends,
        showLegend: inheritedSettings?.showLegend,
      }),
      panelSettings?.showLegend,
    ]),
    tooltipNumberOfRuns: inheritedSettings?.tooltipNumberOfRuns,
    useRunsTableGroupingInPanels: getCascadingSetting([
      inheritedSettings?.useRunsTableGroupingInPanels,
      panelSettings?.useRunsTableGroupingInPanels,
    ]),
  };
};
